// ─── Types ────────────────────────────────────────────────────────────────────

export type ScheduleType =
  | 'learning'
  | 'weekly'
  | 'frequent'
  | 'biweekly'
  | 'irregular'
  | 'rotating'
  | 'manual'

export type ShopRecord = {
  date:         string
  dayOfWeek:    number
  intervalDays: number | null
}

export type ScheduleAnalysis = {
  scheduleType:      ScheduleType
  avgIntervalDays:   number
  confidence:        number
  predictedNextShop: string | null
  description:       string
  isRotating:        boolean
  rotationShiftDays: number | null
}

export type ShopUrgency = {
  daysUntilShop: number | null
  urgency:       'soon' | 'upcoming' | 'later' | 'unknown'
  label:         string
}

// ─── analyzeShoppingSchedule ──────────────────────────────────────────────────

export function analyzeShoppingSchedule(shopDates: string[]): ScheduleAnalysis {
  if (shopDates.length < 2) {
    return {
      scheduleType:      'learning',
      avgIntervalDays:   7,
      confidence:        0,
      predictedNextShop: null,
      description:       'Still learning your schedule',
      isRotating:        false,
      rotationShiftDays: null,
    }
  }

  // Sort dates ascending
  const sorted = [...shopDates]
    .map(d => new Date(d))
    .sort((a, b) => a.getTime() - b.getTime())

  // Calculate intervals between shops
  const intervals: number[] = []
  for (let i = 1; i < sorted.length; i++) {
    const days = Math.round(
      (sorted[i].getTime() - sorted[i - 1].getTime()) /
      (1000 * 60 * 60 * 24)
    )
    intervals.push(days)
  }

  const recentIntervals = intervals.slice(-8)
  const avgInterval     = recentIntervals.reduce((sum, d) => sum + d, 0) / recentIntervals.length

  // Check for rotating pattern — intervals that consistently shift by the same amount each cycle
  const intervalDeltas: number[] = []
  for (let i = 1; i < recentIntervals.length; i++) {
    intervalDeltas.push(recentIntervals[i] - recentIntervals[i - 1])
  }

  const avgDelta = intervalDeltas.length > 0
    ? intervalDeltas.reduce((s, d) => s + d, 0) / intervalDeltas.length
    : 0

  const deltaVariance = intervalDeltas.length > 0
    ? intervalDeltas.reduce((s, d) => s + Math.pow(d - avgDelta, 2), 0) / intervalDeltas.length
    : 0

  const isRotating =
    Math.abs(avgDelta) >= 0.5 &&
    Math.sqrt(deltaVariance) < 2 &&
    intervalDeltas.length >= 3

  // Calculate variance to determine reliability
  const variance = recentIntervals.reduce(
    (sum, interval) => sum + Math.pow(interval - avgInterval, 2), 0
  ) / recentIntervals.length

  const stdDev = Math.sqrt(variance)

  // Determine schedule type from average interval
  let scheduleType: ScheduleType = 'irregular'

  if (isRotating) {
    scheduleType = 'rotating'
  } else if (avgInterval <= 5 && stdDev < 2) {
    scheduleType = 'frequent'
  } else if (avgInterval <= 9 && stdDev < 2.5) {
    scheduleType = 'weekly'
  } else if (avgInterval <= 16 && stdDev < 3) {
    scheduleType = 'biweekly'
  } else {
    scheduleType = 'irregular'
  }

  // Calculate confidence — more data + lower variance = higher confidence
  const dataConfidence        = Math.min(shopDates.length / 8, 1.0)
  const consistencyConfidence = Math.max(0, 1 - stdDev / avgInterval)
  const confidence            = dataConfidence * 0.4 + consistencyConfidence * 0.6

  // Predict next shop date
  const lastShop           = sorted[sorted.length - 1]
  let   predictedInterval  = avgInterval

  if (isRotating && avgDelta !== 0) {
    const lastInterval  = recentIntervals[recentIntervals.length - 1]
    predictedInterval   = lastInterval + avgDelta
    predictedInterval   = Math.max(3, Math.min(predictedInterval, 21))
  }

  const predictedDate = new Date(lastShop)
  predictedDate.setDate(predictedDate.getDate() + Math.round(predictedInterval))

  const predictedNextShop = predictedDate.toISOString().split('T')[0]

  // Generate human-readable description
  const shiftDays = Math.abs(Math.round(avgDelta))
  const descriptions: Record<ScheduleType, string> = {
    learning:  'Still learning your schedule',
    frequent:  `You shop every ${Math.round(avgInterval)} days`,
    weekly:    'You shop roughly once a week',
    biweekly:  'You shop every 1–2 weeks',
    irregular: 'Your shopping schedule varies',
    rotating:  `Your schedule rotates by about ${shiftDays} day${shiftDays !== 1 ? 's' : ''} each cycle`,
    manual:    'Using your custom schedule',
  }

  return {
    scheduleType,
    avgIntervalDays:   avgInterval,
    confidence,
    predictedNextShop,
    description:       descriptions[scheduleType],
    isRotating,
    rotationShiftDays: isRotating ? Math.round(avgDelta) : null,
  }
}

// ─── getEffectivePredictedShop ────────────────────────────────────────────────
// When schedule_type='manual' with a fixed override interval, predicted_next_shop
// is computed once and stored on the households row. After today passes that
// stored date, it sits stale showing a date in the past until something
// rewrites it. This helper rolls the stored prediction forward in
// override_days steps until it lands on or after today, so the UI never shows
// a stale "Predicted next shop: last Tuesday".
//
// For non-manual (auto-detected) schedules we leave the stored value alone —
// the next shop log naturally re-derives it via analyzeShoppingSchedule.

export function getEffectivePredictedShop(
  storedPredicted: string | null,
  scheduleType:    ScheduleType,
  overrideDays:    number | null,
): string | null {
  if (!storedPredicted) return null

  const todayStr = new Date().toISOString().split('T')[0]
  if (storedPredicted >= todayStr) return storedPredicted

  if (scheduleType === 'manual' && overrideDays && overrideDays > 0) {
    const predicted = new Date(storedPredicted + 'T00:00:00')
    const today     = new Date(todayStr     + 'T00:00:00')

    while (predicted < today) {
      predicted.setDate(predicted.getDate() + overrideDays)
    }

    return predicted.toISOString().split('T')[0]
  }

  return storedPredicted
}

// ─── getShopUrgency ───────────────────────────────────────────────────────────

export function getShopUrgency(
  predictedNextShop: string | null,
  confidence:        number,
): ShopUrgency {
  if (!predictedNextShop || confidence < 0.3) {
    return {
      daysUntilShop: null,
      urgency:       'unknown',
      label:         'Learning your schedule',
    }
  }

  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const shopDate = new Date(predictedNextShop)
  shopDate.setHours(0, 0, 0, 0)

  const daysUntilShop = Math.round(
    (shopDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
  )

  if (daysUntilShop <= 1) {
    return {
      daysUntilShop,
      urgency: 'soon',
      label:   daysUntilShop <= 0 ? 'Shop today' : 'Shop tomorrow',
    }
  }

  if (daysUntilShop <= 3) {
    return {
      daysUntilShop,
      urgency: 'soon',
      label:   `Shop in ${daysUntilShop} days`,
    }
  }

  if (daysUntilShop <= 7) {
    return {
      daysUntilShop,
      urgency: 'upcoming',
      label:   `Shop in ${daysUntilShop} days`,
    }
  }

  return {
    daysUntilShop,
    urgency: 'later',
    label:   `Shop in ${daysUntilShop} days`,
  }
}
